import { checkLogin, loginWithRefreshToken } from "Actions";
import { IStoreState } from "Interfaces";
import { Login } from "Pages/Login";
import { SamlLogin } from "Pages/SamlLogin";
import { Logout } from "Pages/Logout";
import { ForgotPassword } from "Pages/Password/ForgotPassword";
import ResetPassword from "Pages/Password/ResetPassword";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter,
  matchPath,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { useMount } from "Hooks";
import { ProtectedRoute } from "Components/ProtectedRoute";
import React, { useCallback, useEffect } from "react";
import { useMemo } from "react";
import { AnimatePresence, motion } from "motion/react";
import { usePrevious } from "react-use";
import { ApiACLRole } from "@incendium/api";
import { useUser } from "Hooks/useUser";
import useProtectedRoutes, { IProtectedRoute } from "Hooks/useProtectedRoutes";
import SingleReportPage from "Pages/Analyse/SingleReportPage";
import AnalyticsProvider from "Providers/AnalyticsProvider";
import DashboardLayout from "Components/DashboardLayout/DashboardLayout";
import withPagetitle from "HoC/withPagetitle";
import withDatepicker from "HoC/withDatepicker";
import withDocumentTitle from "HoC/withDocumentTitle";
import { withClientorProjects } from "HoC/withClientorProjects";
import Loading from "Components/Loading/Loading";
import AnalysePage from "Pages/Analyse/AnalysePage";
import FromToProvider from "Providers/FromToProvider";
import InitialPassword from "Pages/Password/InitialPassword";
import { ChartDescriptionDrawerProvider } from "Providers/ChartDescriptionDrawerProvider";
import AttributionProvider from "Providers/AttributionProvider";
import withAttribution from "HoC/withAttribution";
import ProjectAnalysisCatch from "Router/ProjectAnalysisCatch";
import LightningLanderPage from "Pages/Publish/LightningLanderPage";
import LightningTemplatePage from "Pages/Publish/LightningTemplatePage";
import { sendEvent } from "features/monitoring/services/monitoringService";
import { useSelectedProject } from "Hooks";
import LightningBlockPage from "Pages/Publish/LightningBlockPage";
import SharedComponentBlockPage from "Pages/SuperAdmin/SharedComponentBlockPage";
import { AiProvider } from "features/ai/providers/AiProvider";
import OAuthSync from "Pages/OAuthSync";
import WooCommerceSignup from "Pages/Signup/WooCommerceSignup";
import WooCommerceWelcome from "Pages/Signup/WooCommerceWelcome";
import withSharedToken from "HoC/withSharedToken";
import { useOnboarding } from "features/onboarding/hooks/useOnboarding";
import { Redirect } from "react-router-dom";
import TrialExpiredRedirectListener from "Router/TrialExpiredRedirectListener";
import LightningComponentPage from "Pages/Publish/LightningComponentPage";
import { waitForOnWindow } from "@incendium/inc-ts-helpers";
import ShopifySignupPage from "Pages/Signup/ShopifySignupPage";

const PageTracker = () => {
  const location = useLocation();
  const { selectedProject } = useSelectedProject();

  useEffect(() => {
    // Send page view event to monitoring
    if (selectedProject?.id) {
      const pathWithoutQuery = location.pathname;
      const eventName = `page_visited`;
      try {
        sendEvent(selectedProject.id, eventName, pathWithoutQuery);
      } catch (error) {
        console.error("Error sending event to monitoring", error);
      }
    }
  }, [location.pathname, selectedProject?.id]);

  useEffect(() => {
    waitForOnWindow("inc", (error) => {
      if (error) {
        return;
      }
      if (typeof (window as any).inc === "function") {
        (window as any).inc("run");
      } else {
        console.warn("`inc` is not defined or not a function.");
      }
    });
  }, [location]);

  return null;
};

const CatchAll = ({ children }: React.HTMLAttributes<HTMLDivElement>) => {
  const { user } = useUser();
  const firstPermision = (user?.permissions || [])[0];
  if (!firstPermision) {
    return <Loading fullHeight />;
  }

  if (
    [
      ApiACLRole.ANALYTICS_ADMIN,
      ApiACLRole.ANALYTICS_OBSERVER,
      ApiACLRole.CLIENT_ADMIN,
      ApiACLRole.CLIENT_OBSERVER,
      ApiACLRole.PROJECT_ADMIN,
      ApiACLRole.PROJECT_OBSERVER,
    ].includes(firstPermision.role!)
  ) {
    return <ProjectAnalysisCatch />;
  }

  return <>{children}</>;
};

const OAuthSyncWithSharedToken = withSharedToken(OAuthSync);

export const NRoute = (props: Omit<IProtectedRoute, "name">) => {
  const tokens = useSelector((state: IStoreState) => state.auth.tokens);
  const location = useLocation();
  const prevLocation = usePrevious(location);
  const { user, hasPermission } = useUser(tokens);
  const { selectedProject } = useSelectedProject();
  const { onboarding, fetched, loading: onboardingLoading } = useOnboarding();

  const singleReportPage = matchPath(location.pathname, {
    path: "/reports/:hash",
    exact: true,
    strict: false,
  });

  const isAccountPath = matchPath(location.pathname, {
    path: [
      "/clients/:clientId/projects/:projectId/account",
      "/clients/:clientId/projects/:projectId/account/payment",
      "/clients/:clientId/projects/:projectId/account/ai",
      "/clients/:clientId/projects/:projectId/account/general",
      "/account",
      "/accounts/payment/thank-you/:projectId",
    ],
    exact: true,
    strict: false,
  });

  const PageComponent = useMemo(() => {
    let c = props.component as React.ComponentType<any>;
    c = withDocumentTitle(c);
    if (props.pageTitle) {
      c = withPagetitle(c);
    }

    if (props.withDatepicker) {
      c = withDatepicker(c);
    }
    if (props.withAttr) {
      c = withAttribution(c);
    }

    if (props.withClients) {
      c = withClientorProjects(c);
    }
    return c;
  }, [
    props.withClients,
    props.component,
    props.pageTitle,
    props.withDatepicker,
    props.withAttr,
  ]);

  const renderRoute = useCallback(() => {
    // Compute path matches inside the callback to ensure we use current location
    const isGetStartedPath = matchPath(location.pathname, {
      path: "/clients/:clientId/projects/:projectId/get-started",
      exact: true,
      strict: false,
    });

    const isProjectPath = matchPath(location.pathname, {
      path: "/clients/:clientId/projects/:projectId",
      exact: false,
      strict: false,
    });

    const { component, ...routeProps } = props;
    const route = (
      <Route {...routeProps}>
        <PageComponent
          title={props.pageTitle}
          subTitle={props.pageSubTitle}
          documentTitle={props.documentTitle}
        />
      </Route>
    );

    // Check onboarding status for project routes (except get-started page, single report page, and account paths)
    // This check happens before minRole check to ensure all project routes are protected
    if (
      isProjectPath &&
      !isGetStartedPath &&
      !singleReportPage &&
      !isAccountPath &&
      selectedProject &&
      fetched &&
      !onboardingLoading
    ) {
      if (
        !hasPermission(ApiACLRole.SUPER_ADMIN) &&
        Object.keys(onboarding).length > 0 &&
        onboarding?.id &&
        !onboarding.approvedDate
        // && onboarding.publishedDate
      ) {
        return (
          <Redirect
            to={`/clients/${selectedProject.clientId}/projects/${selectedProject.id}/get-started`}
          />
        );
      }
    }

    // Show loading while onboarding is being fetched for project routes
    if (
      isProjectPath &&
      !isGetStartedPath &&
      !singleReportPage &&
      !isAccountPath &&
      selectedProject &&
      (!fetched || onboardingLoading)
    ) {
      return <Loading fullHeight />;
    }

    // if is single report page, we manage its auth from within
    if (!props.minRole || !!singleReportPage || !!isAccountPath) {
      return route;
    }

    return tokens && user && hasPermission(props.minRole) ? (
      route
    ) : tokens && user ? (
      <CatchAll>{route}</CatchAll>
    ) : (
      <Loading fullHeight text="..." />
    );
  }, [
    props,
    tokens,
    PageComponent,
    hasPermission,
    user,
    singleReportPage,
    isAccountPath,
    location.pathname,
    selectedProject,
    onboarding,
    fetched,
    onboardingLoading,
  ]);

  return (
    <motion.div
      style={{ height: "100%" }}
      initial={{ opacity: 0 }}
      exit={
        !prevLocation ||
        (!(
          prevLocation.pathname.includes("addlocation") &&
          location.pathname.includes("tagpages")
        ) &&
          !(
            prevLocation.pathname.includes("tagpages") &&
            location.pathname.includes("addlocation")
          ))
          ? {
              opacity: 0,
              x: 200,
              transition: {
                x: {
                  delay: 0.12,
                },
              },
            }
          : { opacity: 0 }
      }
      animate={{ opacity: 1 }}
    >
      {renderRoute()}
    </motion.div>
  );
};

const DashboardRoutes = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  return (
    <AiProvider>
      <ChartDescriptionDrawerProvider>
        <DashboardLayout hideBreadcrumbs>
          <AnimatePresence mode="wait">
            {/* <FromToProvider> */}
            <AnalyticsProvider>
              <Switch location={location} key={location.key}>
                {children as any}
              </Switch>
            </AnalyticsProvider>
            {/* </FromToProvider> */}
          </AnimatePresence>
        </DashboardLayout>
      </ChartDescriptionDrawerProvider>
    </AiProvider>
  );
};

/**
 * Must live inside BrowserRouter so useHistory/useLocation are available.
 * Handles the ?refresh_token= query param injected by the SAML ACS redirect,
 * stripping it via react-router history so useLocation() updates everywhere.
 */
const SamlTokenHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const loginStatus = useSelector((state: IStoreState) => state.auth.status);

  useMount(() => {
    const params = new URLSearchParams(search);
    const samlRefreshToken = params.get("refresh_token");

    if (samlRefreshToken) {
      // Strip the token via react-router so useLocation() updates downstream.
      params.delete("refresh_token");
      const newSearch = params.toString();
      history.replace(pathname + (newSearch ? `?${newSearch}` : ""));

      dispatch(loginWithRefreshToken(samlRefreshToken));
    } else if (loginStatus === "INIT") {
      dispatch(checkLogin());
    }
  });

  return null;
};

export const Router = () => {
  const protectedRoutes = useProtectedRoutes();

  return (
    <>
      <BrowserRouter>
        <SamlTokenHandler />
        <PageTracker />
        <TrialExpiredRedirectListener />
        <Switch>
          <Route exact path="/login" component={Login} />
          <Route
            exact
            path="/signup/woocommerce"
            component={WooCommerceSignup}
          />
          <Route exact path="/signup/shopify" component={ShopifySignupPage} />
          <Route exact path="/login/saml" component={SamlLogin} />
          <Route
            exact
            path="/password/forgot-password"
            component={ForgotPassword}
          />
          <Route
            path="/password/reset-password/:code"
            component={ResetPassword}
          />
          <Route path="/verify-email/:code" component={InitialPassword} />
          <Route path="/woo-welcome/:code" component={WooCommerceWelcome} />
          <Route exact path="/logout" component={Logout} />
          <Route
            exact
            path="/oauth-sync/:token"
            component={OAuthSyncWithSharedToken}
          />
          <Route exact path="/oauth-sync" component={OAuthSync} />
          <NRoute
            exact={true}
            path="/reports/:hash"
            withClients
            component={SingleReportPage}
            minRole={ApiACLRole.ANALYTICS_OBSERVER}
          />

          <ProtectedRoute path="/clients/:clientId/projects/:projectId/publish/lightning-landing-pages/:componentId">
            <NRoute
              exact={true}
              path="/clients/:clientId/projects/:projectId/publish/lightning-landing-pages/:componentId"
              withClients
              component={LightningLanderPage}
            />
          </ProtectedRoute>
          <ProtectedRoute path="/clients/:clientId/projects/:projectId/publish/lightning-landing-templates/:componentId">
            <NRoute
              exact={true}
              path="/clients/:clientId/projects/:projectId/publish/lightning-landing-templates/:componentId"
              withClients
              component={LightningTemplatePage}
            />
          </ProtectedRoute>
          <ProtectedRoute path="/clients/:clientId/projects/:projectId/publish/lightning-landing-blocks/:componentId">
            <NRoute
              exact={true}
              path="/clients/:clientId/projects/:projectId/publish/lightning-landing-blocks/:componentId"
              withClients
              component={LightningBlockPage}
            />
          </ProtectedRoute>
          <ProtectedRoute path="/clients/:clientId/projects/:projectId/publish/lightning-landing-components/:componentId">
            <NRoute
              exact={true}
              path="/clients/:clientId/projects/:projectId/publish/lightning-landing-components/:componentId"
              withClients
              component={LightningComponentPage}
            />
          </ProtectedRoute>
          <ProtectedRoute path="/admin/shared-component-blocks/:componentId">
            <NRoute
              exact={true}
              path="/admin/shared-component-blocks/:componentId"
              withClients
              component={SharedComponentBlockPage}
              minRole={ApiACLRole.SUPER_ADMIN}
            />
          </ProtectedRoute>

          <ProtectedRoute path="/">
            <DashboardRoutes>
              <NRoute
                path="/clients/:clientId/projects/:projectId/analyse/*"
                component={() => (
                  <FromToProvider>
                    <AttributionProvider>
                      <AnalysePage />
                    </AttributionProvider>
                  </FromToProvider>
                )}
              />
              {(protectedRoutes.routes || [])
                .filter((p) => !p.path?.includes("analyse/"))
                .map((pr, i) => (
                  <NRoute
                    key={i}
                    exact={pr.exact}
                    path={pr.path}
                    withClients={pr.withClients}
                    component={pr.component}
                    minRole={pr.minRole}
                    pageTitle={pr.pageTitle}
                    pageSubTitle={pr.pageSubTitle}
                    documentTitle={pr.documentTitle}
                    withDatepicker={pr.withDatepicker}
                    withAttr={pr.withAttr}
                    withFromTo={pr.withFromTo}
                  />
                ))}
            </DashboardRoutes>
          </ProtectedRoute>
        </Switch>
      </BrowserRouter>
    </>
  );
};
